import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom, useTable } from 'src/components/table';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Roles } from 'src/types/User';
import { GetOrganizationUsersDto } from 'woofwoof-api';
import Image from '../../../../../../components/image';
import { ROLES } from '../OrganizationInvites/OrganizationInvitesAddModal';
import { translateKey } from 'src/locales/translate';


interface Row {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: GetOrganizationUsersDto['roles'];
  avatar?: string;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center';
  render?: (row: Row) => ReactNode;
}

interface OrganizationUsersListTableProps {
  users: GetOrganizationUsersDto[];
  isError: boolean;
  isLoading: boolean;
}

export default function OrganizationUsersListTable({
  users,
  isError,
  isLoading,
}: OrganizationUsersListTableProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();

  const handleOnRowClick = (row: Row) => {
    // Check if user has professional role
    const isProfessional = row.roles.includes(Roles.professional as any);
    if (isProfessional && organizationId) {
      navigate(
        PATH_DASHBOARD.general.organizations.professionalDetails(
          organizationId,
          row._id,
        ),
      );
    }
  };

  const tableData: Row[] =
    //TODO: Add proper type
    users.map((user) => ({
      _id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roles: user.roles,
      avatar: user.avatar,
    })) || [];

  const COLUMNS: Column[] = [
    {
      id: 'firstName',
      label: translateKey("hardcoded.imie_i_nazwisko"),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">
            {value.firstName} {value.lastName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'email',
      label: 'E-mail',
      render: (value: Row) => value.email,
    },
    {
      id: 'roles',
      label: 'Role',
      render: (value: Row) =>
        value.roles
          .map((role) => ROLES.find((r) => r.value === role)?.label)
          .join(', '),
    },
  ];
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 25,
  });

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, rowIndex) => (
                <TableRow
                  hover
                  key={rowIndex}
                  onClick={() => handleOnRowClick(row)}
                  sx={{
                    cursor: row.roles.includes(Roles.professional as any)
                      ? 'pointer'
                      : 'default',
                  }}
                >
                  {COLUMNS.map((column, index) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.render ? column.render(row) : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {!isLoading && tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              {isLoading ? (
                <CircularProgress size={48} />
              ) : (
                <>
                  <Image
                    sx={{ width: '200px' }}
                    alt="empty waiting room"
                    src="/assets/images/empty_table.svg"
                  />
                  <Typography>
                    {isError
                      ? translateKey("hardcoded.nie_moglismy_pobrac_uzytkownikow")
                      : translateKey("hardcoded.brak_uzytkownikow")}
                  </Typography>
                </>
              )}
            </Stack>
          </>
        )}
      </TableContainer>

      <TablePaginationCustom
        count={tableData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
}
